import { z } from 'zod'

import { makeGenericZodErrorMessage } from '@/utils/make-generic-zod-error-message'

const caneOptionsSchema = z
  .object({
    gramineas_porte_alto: z.boolean(),
    gramineas_porte_baixo: z.boolean(),
    mamona: z.boolean(),
    trepadeiras: z.boolean(),
    daninhas_indefinidas: z.boolean(),
    outras_folhas_largas: z.boolean(),
  })
  .refine((data) => Object.values(data).some((value) => value), {
    message: 'Campo obrigatório',
  })

const soybeanOptionsSchema = z
  .object({
    gramineas_porte_medio: z.boolean(),
    gramineas_porte_baixo: z.boolean(),
    buva: z.boolean(),
    daninhas_indefinidas: z.boolean(),
    outras_folhas_largas: z.boolean(),
    capim_amargoso: z.boolean(),
  })
  .refine((data) => Object.values(data).some((value) => value), {
    message: 'Campo obrigatório',
  })

const sortOptionsSchema = z.enum([
  'demand_name',
  '-demand_name',
  'created_at',
  '-created_at',
  'infestation_percentage',
  '-infestation_percentage',
  'total_area',
  '-total_area',
])

const periodOptionsSchema = z.object({
  options: z.enum(['7', '30', '90']),
})

const dateSchema = z.coerce
  .date(makeGenericZodErrorMessage('A data é obrigatória.'))
  .min(new Date('1900-01-01'), { message: 'Data muito antiga.' })
  .max(new Date(), { message: 'Data futura.' })
  .transform((value) => value.toJSON().split('T')[0])

const periodOptionsCustomSchema = z.object({
  options: z.enum(['personalized']),
  personalized: z
    .object({
      start_date: dateSchema,
      end_date: dateSchema,
    })
    .refine((data) => data.start_date <= data.end_date, {
      message: 'Data final deve ser maior que a data inicial',
      path: ['end_date'],
    }),
})

const commonSchema = z.object({
  sort_options: sortOptionsSchema,
  period: z.discriminatedUnion('options', [
    periodOptionsSchema,
    periodOptionsCustomSchema,
  ]),
})

const caneSchema = z
  .object({
    culture: z.literal('cane', makeGenericZodErrorMessage('Campo obrigatório')),
    cane: z.boolean(),
    cane_options: caneOptionsSchema,
  })
  .merge(commonSchema)

const soybeanSchema = z
  .object({
    culture: z.literal(
      'soybean',
      makeGenericZodErrorMessage('Campo obrigatório'),
    ),
    soybean: z.boolean(),
    soybean_options: soybeanOptionsSchema,
  })
  .merge(commonSchema)

const allSchema = z
  .object({
    culture: z.literal('all', makeGenericZodErrorMessage('Campo obrigatório')),
    cane: z.boolean(),
    soybean: z.boolean(),
    cane_options: caneOptionsSchema,
    soybean_options: soybeanOptionsSchema,
  })
  .merge(commonSchema)

export const schema = z.discriminatedUnion(
  'culture',
  [caneSchema, soybeanSchema, allSchema],
  makeGenericZodErrorMessage('Campo obrigatório'),
)

type CommonSchemaType = Exclude<z.infer<typeof commonSchema>, 'period'> & {
  period: z.mergeTypes<
    z.infer<typeof periodOptionsSchema>,
    z.infer<typeof periodOptionsCustomSchema>
  >
}

export type AllNewReportForm = z.infer<typeof allSchema> & CommonSchemaType
export type DefaultNewReportForm = z.infer<typeof schema>
